/**
 * Utilities for transforming data between backend (snake_case) and frontend (camelCase) formats
 */

/**
 * Converts a snake_case string to camelCase
 *
 * @param str - String in snake_case format
 * @returns String in camelCase format
 *
 * @example
 * snakeToCamel('redirect_type') // 'redirectType'
 * snakeToCamel('created_at') // 'createdAt'
 */
export function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

/**
 * Converts a camelCase string to snake_case
 *
 * @param str - String in camelCase format
 * @returns String in snake_case format
 *
 * @example
 * camelToSnake('redirectType') // 'redirect_type'
 * camelToSnake('createdAt') // 'created_at'
 */
export function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

/**
 * Transforms an object from snake_case keys to camelCase keys
 *
 * @param obj - Object with snake_case keys
 * @returns Object with camelCase keys
 *
 * @example
 * transformSnakeToCamel({ redirect_type: '301', created_at: '2022-01-01' })
 * // { redirectType: '301', createdAt: '2022-01-01' }
 */
export function transformSnakeToCamel<T extends Record<string, any>>(
  obj: T
): Record<string, any> {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return obj;
  }

  const transformed: Record<string, any> = {};

  Object.keys(obj).forEach((key) => {
    const camelKey = snakeToCamel(key);
    const value = obj[key];

    if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
      transformed[camelKey] = transformSnakeToCamel(value);
    } else if (Array.isArray(value)) {
      transformed[camelKey] = value.map((item) =>
        typeof item === 'object' && item !== null && !(item instanceof Date)
          ? transformSnakeToCamel(item)
          : item
      );
    } else {
      transformed[camelKey] = value;
    }
  });

  return transformed;
}

/**
 * Transforms an object from camelCase keys to snake_case keys
 *
 * @param obj - Object with camelCase keys
 * @returns Object with snake_case keys
 *
 * @example
 * transformCamelToSnake({ redirectType: '301', createdAt: '2022-01-01' })
 * // { redirect_type: '301', created_at: '2022-01-01' }
 */
export function transformCamelToSnake<T extends Record<string, any>>(
  obj: T
): Record<string, any> {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return obj;
  }

  const transformed: Record<string, any> = {};

  Object.keys(obj).forEach((key) => {
    const snakeKey = camelToSnake(key);
    const value = obj[key];

    if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
      transformed[snakeKey] = transformCamelToSnake(value);
    } else if (Array.isArray(value)) {
      transformed[snakeKey] = value.map((item) =>
        typeof item === 'object' && item !== null && !(item instanceof Date)
          ? transformCamelToSnake(item)
          : item
      );
    } else {
      transformed[snakeKey] = value;
    }
  });

  return transformed;
}

